import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { CountryConfig } from '@shared/types';
import {
  detectCountryCode,
  detectCurrencyCode,
  formatPriceFromBase,
  type SupportedCurrency,
  type SupportedCountry,
} from '@/lib/currency';
import { useFarmStore } from '@/store/useFarmStore';

export function useCurrency() {
  const selectedCountry = useFarmStore((s) => s.selectedCountry);
  
  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => api<CountryConfig[]>('/api/countries'),
    staleTime: 5 * 60 * 1000,
  });
  
  const countryMap = useMemo(() => {
    if (!countries) return null;
    return Object.fromEntries(countries.map((c) => [c.code, c]));
  }, [countries]);
  
  const countryCode = useMemo<SupportedCountry>(() => {
    if (selectedCountry) return selectedCountry;
    const detected = detectCountryCode();
    if (countryMap && countryMap[detected]) return detected;
    return countries?.[0]?.code || detected;
  }, [selectedCountry, countryMap, countries]);

  const selectedCountryConfig = countryMap?.[countryCode];

  const currency = useMemo<SupportedCurrency>(() => {
    if (selectedCountryConfig?.currency) return selectedCountryConfig.currency;
    return detectCurrencyCode();
  }, [selectedCountryConfig]);

  const formatFromBase = (amount: number, options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }) => {
    if (selectedCountryConfig?.exchangeRate && currency) {
      const converted = amount * selectedCountryConfig.exchangeRate;
      const locale = selectedCountryConfig.locale || 'en-US';
      
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: options?.minimumFractionDigits ?? 0,
        maximumFractionDigits: options?.maximumFractionDigits ?? 2,
      }).format(converted);
    }
    return formatPriceFromBase(amount, currency, options);
  };

  return {
    countryCode,
    currency,
    formatFromBase,
    countries: countries || [],
    selectedCountryConfig,
  };
}
